import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Page,
  Card,
  DataTable,
  Badge,
  Button,
  TextField,
  InlineStack,
  Text,
  Pagination,
  BlockStack,
  SkeletonBodyText,
  EmptyState,
  Box,
  Modal,
  Banner,
  Checkbox,
} from '@shopify/polaris';
import { returnsApi } from '@/services';
import type { ReturnQueueItem } from '@/types';
import { orderNumber } from '@/utils/orderNumber';

export default function ReturnsPending() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [scanValue, setScanValue] = useState('');
  const [scanError, setScanError] = useState('');
  const [selectedRental, setSelectedRental] = useState<ReturnQueueItem | null>(null);
  const [selectedSerials, setSelectedSerials] = useState<Set<string>>(new Set());
  const [showProcessModal, setShowProcessModal] = useState(false);
  const limit = 20;

  const { data, isLoading, error } = useQuery({
    queryKey: ['pending-returns', page],
    queryFn: () => returnsApi.getPendingReturns({ page, limit }),
  });

  const processReturnMutation = useMutation({
    mutationFn: (data: { rentalId: string; serialNumbers: string[] }) =>
      returnsApi.processReturn({
        rentalId: data.rentalId,
        serialNumbers: data.serialNumbers,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-returns'] });
      setShowProcessModal(false);
      setSelectedRental(null);
      setSelectedSerials(new Set());
    },
  });

  const scanMutation = useMutation({
    mutationFn: (value: string) => returnsApi.scan(value),
    onSuccess: (result) => {
      setSelectedRental(result.rental);
      setScanError('');
      setScanValue('');
      setShowProcessModal(true);
    },
    onError: () => {
      setScanError('No rental found with this tracking number or serial');
    },
  });

  const handleScan = useCallback(() => {
    if (scanValue.trim()) {
      scanMutation.mutate(scanValue.trim());
    }
  }, [scanValue, scanMutation]);

  const handleProcessReturn = useCallback((rental: ReturnQueueItem) => {
    setSelectedRental(rental);
    setSelectedSerials(new Set());
    setShowProcessModal(true);
  }, []);

  const handleToggleSerial = useCallback((serial: string) => {
    setSelectedSerials((prev) => {
      const next = new Set(prev);
      if (next.has(serial)) {
        next.delete(serial);
      } else {
        next.add(serial);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (!selectedRental) return;
    const allSerials = selectedRental.items.flatMap((item) => item.assignedSerials);
    if (selectedSerials.size === allSerials.length) {
      setSelectedSerials(new Set());
    } else {
      setSelectedSerials(new Set(allSerials));
    }
  }, [selectedRental, selectedSerials]);

  const handleConfirmReturn = useCallback(() => {
    if (!selectedRental || selectedSerials.size === 0) return;
    processReturnMutation.mutate({
      rentalId: selectedRental.id,
      serialNumbers: Array.from(selectedSerials),
    });
  }, [selectedRental, selectedSerials, processReturnMutation]);

  const getOverdueBadge = (daysOverdue: number) => {
    if (daysOverdue <= 0) {
      return <Badge tone="success">On Time</Badge>;
    } else if (daysOverdue <= 7) {
      return <Badge tone="warning">{`${daysOverdue} days overdue`}</Badge>;
    } else {
      return <Badge tone="critical">{`${daysOverdue} days overdue`}</Badge>;
    }
  };

  const rows = (data?.data || []).map((item) => [
    <Button key={item.id} variant="plain" onClick={() => navigate(`/rentals/${item.id}`)}>
      {orderNumber(item)}
    </Button>,
    item.clinic?.name || item.patientName || 'N/A',
    item.rentalEndDate ? new Date(item.rentalEndDate).toLocaleDateString() : 'N/A',
    String(item.items.flatMap((i) => i.assignedSerials).length),
    getOverdueBadge(item.daysOverdue),
    <Button key={`action-${item.id}`} onClick={() => handleProcessReturn(item)}>
      Process Return
    </Button>,
  ]);

  if (error) {
    return (
      <Page title="Pending Returns">
        <Card>
          <EmptyState
            heading="Error loading returns"
            image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
          >
            <p>Please try again later.</p>
          </EmptyState>
        </Card>
      </Page>
    );
  }

  const allSerials = selectedRental?.items.flatMap((item) => item.assignedSerials) || [];

  return (
    <Page
      title="Pending Returns"
      primaryAction={{
        content: 'Scan Return',
        onAction: () => {},
      }}
    >
      {/* Scan Section */}
      <Box paddingBlockEnd="400">
        <Card>
          <BlockStack gap="300">
            <Text as="h2" variant="headingMd">
              Scan Return
            </Text>
            <InlineStack gap="200">
              <div style={{ flex: 1 }} onKeyDown={(e) => e.key === 'Enter' && handleScan()}>
                <TextField
                  label="Scan barcode"
                  labelHidden
                  placeholder="Scan tracking number or serial..."
                  value={scanValue}
                  onChange={setScanValue}
                  autoComplete="off"
                  error={scanError}
                />
              </div>
              <Button variant="primary" onClick={handleScan} loading={scanMutation.isPending}>
                Search
              </Button>
            </InlineStack>
          </BlockStack>
        </Card>
      </Box>

      {/* Returns List */}
      <Card padding="0">
        {isLoading ? (
          <Box padding="400">
            <SkeletonBodyText lines={10} />
          </Box>
        ) : rows.length === 0 ? (
          <Box padding="800">
            <EmptyState
              heading="No rentals pending return"
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            >
              <p>All rentals have been returned.</p>
            </EmptyState>
          </Box>
        ) : (
          <>
            <DataTable
              columnContentTypes={['text', 'text', 'text', 'numeric', 'text', 'text']}
              headings={['Order', 'Customer', 'End Date', 'Items', 'Status', 'Action']}
              rows={rows}
            />
            {data && data.meta.totalPages > 1 && (
              <Box padding="400">
                <InlineStack align="center">
                  <Pagination
                    hasPrevious={page > 1}
                    hasNext={page < data.meta.totalPages}
                    onPrevious={() => setPage((p) => p - 1)}
                    onNext={() => setPage((p) => p + 1)}
                  />
                </InlineStack>
              </Box>
            )}
          </>
        )}
      </Card>

      {/* Process Return Modal */}
      <Modal
        open={showProcessModal}
        onClose={() => {
          setShowProcessModal(false);
          setSelectedRental(null);
          setSelectedSerials(new Set());
        }}
        title={`Process Return - ${selectedRental ? orderNumber(selectedRental) : ''}`}
        primaryAction={{
          content: 'Confirm Return',
          onAction: handleConfirmReturn,
          disabled: selectedSerials.size === 0,
          loading: processReturnMutation.isPending,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setShowProcessModal(false);
              setSelectedRental(null);
            },
          },
        ]}
      >
        <Modal.Section>
          {selectedRental && (
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text as="p">
                  Customer: {selectedRental.clinic?.name || selectedRental.patientName}
                </Text>
                <Button variant="plain" onClick={handleSelectAll}>
                  {selectedSerials.size === allSerials.length ? 'Deselect All' : 'Select All'}
                </Button>
              </InlineStack>

              <Banner>
                Select the items being returned. They will be placed in QC hold for inspection.
              </Banner>

              <BlockStack gap="200">
                {selectedRental.items.map((item) => (
                  <Card key={item.id}>
                    <BlockStack gap="200">
                      <Text as="p" fontWeight="semibold">
                        {item.productName}
                      </Text>
                      {item.assignedSerials.map((serial) => (
                        <Checkbox
                          key={serial}
                          label={serial}
                          checked={selectedSerials.has(serial)}
                          onChange={() => handleToggleSerial(serial)}
                        />
                      ))}
                    </BlockStack>
                  </Card>
                ))}
              </BlockStack>

              <Text as="p" tone="subdued">
                Selected: {selectedSerials.size} of {allSerials.length} items
              </Text>
            </BlockStack>
          )}
        </Modal.Section>
      </Modal>
    </Page>
  );
}
